import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Sparkles, MessageSquare, Target, Zap, Gift,
  ChevronRight, ArrowRight, BrainCircuit, Mic2, FileText,
  TrendingUp, ShieldCheck, Clock
} from "lucide-react";
import { useEnroll } from "@/context/EnrollContext";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.55, ease: [0.4, 0, 0.2, 1] },
  }),
};

const WHY_CHINI = [
  {
    icon: BrainCircuit,
    title: "Executive Focused",
    desc: "Built for mid to senior professionals who need sharp, realistic, high stakes interview prep.",
  },
  {
    icon: Mic2,
    title: "Trained on Real Coaching",
    desc: "CHINI isn't a generic chatbot. She's modeled on Dr. Chinmayee Abbey's proven methods with executives across industries.",
  },
  {
    icon: Target,
    title: "Interview Only, Laser Focused",
    desc: "No distractions. Just targeted help to crack any interview: behavioral, case, technical, or leadership round.",
  },
  {
    icon: Gift,
    title: "Free for Now",
    desc: "Premium coaching quality at zero cost. Ask as many questions as you want, anytime.",
  },
];

const WHAT_YOU_CAN_DO = [
  { icon: MessageSquare, label: "Practice real questions", eg: "Give me five challenging behavioral questions for a VP of Analytics role." },
  { icon: FileText, label: "Get answer frameworks", eg: "How should I structure my introduction for a CXO interview?" },
  { icon: Mic2, label: "Simulate interviews", eg: "Run a mock leadership interview for a Director of Product role." },
  { icon: Sparkles, label: "Refine your stories", eg: "Help me turn this project into a strong STAR story." },
  { icon: Zap, label: "Handle curveballs", eg: 'What\'s the best way to answer the question: "Why should we hire you over internal candidates?"' },
  { icon: TrendingUp, label: "Negotiate with confidence", eg: "Draft a response to a lowball offer after the final interview round." },
];

const HOW_IT_WORKS = [
  { num: "01", title: 'Click "Try CHINI Now"', desc: "One click. No signup, no forms, no payments." },
  { num: "02", title: "Type your question or scenario", desc: "Be as specific as you like. The more context, the sharper the answer." },
  { num: "03", title: "Get clear, actionable guidance", desc: "Examples, frameworks, phrasing, and strategy, delivered instantly." },
  { num: "04", title: "Iterate until it's perfect", desc: "Ask follow ups, refine answers, or run another mock round. Unlimited." },
];

const DR_STATS = [
  { value: "20+", label: "Years of Professional Experience" },
  { value: "12+", label: "Years in Career Coaching" },
  { value: "100+", label: "Workshops Delivered" },
];

export const Curriculum = () => {
  const { openEnroll } = useEnroll();

  return (
    <section id="curriculum" className="relative overflow-hidden bg-white">

      {/* ── HERO INTRO ─────────────────────────────────────────────────── */}
      <div className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] py-24 lg:py-32 overflow-hidden">
        {/* Glow orbs */}
        <div className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-primary/20 blur-[100px]" />
        <div className="pointer-events-none absolute bottom-0 right-0 h-64 w-64 rounded-full bg-primary/10 blur-[80px]" />

        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Free · Instant · Executive-Grade
            </span>
          </motion.div>

          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="mt-6 font-display text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            Crack Your Next Interview
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}>
            <p className="mt-3 text-xl font-semibold text-primary sm:text-2xl">
              Meet CHINI, your always on Interview Coach
            </p>
            <p className="mt-5 max-w-2xl mx-auto text-base leading-relaxed text-white/70 sm:text-lg">
              Trained on Dr. Chinmayee Abbey's 20+ years of Executive Career Coaching experience. Ask anything. Practice endlessly. Walk into every interview with clarity, confidence, and a winning strategy.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm font-semibold text-white/60">
              {["100% Free", "No Signup", "Unlimited Questions"].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-primary" /> {t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3} variants={fadeUp} className="mt-10">
            <Button
              variant="hero"
              size="xl"
              onClick={() => openEnroll("Professional")}
              className="group px-10 text-base shadow-lg shadow-primary/30"
            >
              Try CHINI Now
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* ── WHY CHINI ──────────────────────────────────────────────────── */}
      <div className="bg-secondary/30 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="mx-auto max-w-2xl text-center"
          >
            <span className="inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
              Why CHINI?
            </span>
            <h2 className="section-title mt-4 text-foreground">
              Not Just Another <span className="text-gradient-primary">Chatbot</span>
            </h2>
          </motion.div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_CHINI.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }}
                  custom={i} variants={fadeUp}
                  className="group relative rounded-2xl border border-border bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all group-hover:bg-primary group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-foreground">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── WHAT YOU CAN DO ────────────────────────────────────────────── */}
      <div className="bg-[#1a1a2e] py-20 lg:py-28 relative overflow-hidden">
        <div className="pointer-events-none absolute top-0 right-0 h-80 w-80 rounded-full bg-primary/10 blur-[100px]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="mx-auto max-w-2xl text-center"
          >
            <span className="inline-block rounded-full bg-primary/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
              What You Can Do
            </span>
            <h2 className="section-title mt-4 text-white">
              CHINI Is Your Personal{" "}
              <span className="text-gradient-primary">Interview Strategist</span>
            </h2>
            <p className="mt-4 text-base text-white/60">
              Type anything. Get instant, tailored guidance. Iterate until it's perfect.
            </p>
          </motion.div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHAT_YOU_CAN_DO.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.label}
                  initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }}
                  custom={i % 3} variants={fadeUp}
                  className="group flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-6 transition-all hover:border-primary/50 hover:bg-white/10"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/20 text-primary group-hover:bg-primary group-hover:text-white transition-all">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-display text-base font-semibold text-white">{item.label}</p>
                    <p className="mt-1.5 text-sm italic leading-relaxed text-white/50">{item.eg}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ───────────────────────────────────────────────── */}
      <div className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="mx-auto max-w-2xl text-center"
          >
            <span className="inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
              How It Works
            </span>
            <h2 className="section-title mt-4 text-foreground">
              Four Steps to <span className="text-gradient-primary">Interview Confidence</span>
            </h2>
            <p className="mt-3 text-base text-muted-foreground">No forms. No payments. No limits.</p>
          </motion.div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((step, i) => (
              <motion.div
                key={step.num}
                initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }}
                custom={i} variants={fadeUp}
                className="relative rounded-2xl border border-border bg-secondary/30 p-7"
              >
                {/* Connector arrow */}
                {i < HOW_IT_WORKS.length - 1 && (
                  <ChevronRight className="absolute -right-3 top-1/2 z-10 hidden h-6 w-6 -translate-y-1/2 text-primary/40 lg:block" />
                )}
                <span className="font-display text-4xl font-extrabold text-primary/20">{step.num}</span>
                <h3 className="mt-3 font-display text-lg font-bold text-foreground">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} custom={4} variants={fadeUp}
            className="mt-12 text-center"
          >
            <Button
              variant="hero"
              size="xl"
              onClick={() => openEnroll("Professional")}
              className="group px-10 text-base"
            >
              Try CHINI Now
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* ── BUILT BY DR. CHINMAYEE ABBEY ───────────────────────────────── */}
      <div className="bg-secondary/30 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
            <div className="grid lg:grid-cols-2">
              {/* Left - content */}
              <div className="p-10 lg:p-14">
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
                  <span className="inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
                    Built By
                  </span>
                  <h2 className="section-title mt-4 text-foreground">
                    Dr. Chinmayee Abbey
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    An Executive Career Coach with deep expertise in behavioral science, interview strategy, and executive storytelling, with a track record of helping mid to senior professionals land roles across industries.
                  </p>
                  <ul className="mt-6 space-y-3">
                    {[
                      "20+ years of Executive Career Coaching & Leadership experience",
                      "100+ workshops delivered for leadership and career development",
                      "Deep expertise in behavioral science, interview strategy, and executive storytelling",
                      "Proven track record with mid to senior professionals across industries",
                    ].map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-sm italic text-muted-foreground border-l-4 border-primary/40 pl-4">
                    CHINI distills this experience into a fast, focused, interview only AI coach, so you can practice like you're working with Dr. Chinmayee Abbey anytime.
                  </p>
                </motion.div>
              </div>

              {/* Right - stats + CTA */}
              <div className="flex flex-col justify-center bg-gradient-to-br from-[#1a1a2e] to-[#0f3460] p-10 lg:p-14">
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}>
                  <p className="text-xs font-bold uppercase tracking-widest text-primary">By the numbers</p>
                  <div className="mt-6 space-y-6">
                    {DR_STATS.map((s) => (
                      <div key={s.label} className="flex items-center gap-5">
                        <span className="font-display text-4xl font-extrabold text-primary">{s.value}</span>
                        <span className="text-sm text-white/60">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp} className="mt-10">
                  <h3 className="font-display text-2xl font-bold text-white">Ready to Own Your Interview?</h3>
                  <p className="mt-3 text-sm text-white/60">
                    Free, instant, and unlimited. Your next offer begins with the next question you ask.
                  </p>
                  <Button
                    variant="hero"
                    size="lg"
                    onClick={() => openEnroll("Professional")}
                    className="group mt-7 w-full justify-center text-base"
                  >
                    Try CHINI Now
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <div className="mt-4 flex items-center justify-center gap-4 text-xs text-white/40">
                    <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Free now</span>
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-primary" /> Instant access</span>
                    <span className="flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-primary" /> No signup</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};
